import React, { useState } from 'react'
import {
  XMarkIcon,
  DocumentIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

interface FileViewerProps {
  isOpen: boolean
  onClose: () => void
  fileUrl: string
  fileName?: string
  title?: string
}

export default function FileViewer({ 
  isOpen, 
  onClose, 
  fileUrl, 
  fileName = 'Document', 
  title = 'File Viewer' 
}: FileViewerProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  if (!isOpen) return null

  // Get file extension to determine file type
  const getFileExtension = (url: string) => {
    return url.split('.').pop()?.toLowerCase() || ''
  }

  const fileExtension = getFileExtension(fileUrl)
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExtension)
  const isPdf = fileExtension === 'pdf'
  const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExtension)

  const handleDownload = () => {
    try {
      const link = document.createElement('a')
      link.href = fileUrl
      link.download = fileName
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success('Download started')
    } catch (error) {
      console.error('Download error:', error)
      toast.error('Failed to download file')
    }
  }

  const handleOpenInNewTab = () => {
    window.open(fileUrl, '_blank')
  }

  const renderFileContent = () => {
    if (isImage) {
      return (
        <div className="flex-1 flex items-center justify-center p-2">
          <img
            src={fileUrl}
            alt={fileName}
            className="max-w-full max-h-full object-contain rounded-lg"
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false)
              setError(true)
            }}
          />
        </div>
      )
    }

    if (isPdf) {
      return (
        <div className="flex-1 flex flex-col min-h-0">
          <iframe
            src={fileUrl}
            className="flex-1 w-full border-0 rounded-lg min-h-[600px]"
            title={fileName}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false)
              setError(true)
            }}
          />
        </div>
      )
    }

    if (isVideo) {
      return (
        <div className="flex-1 flex items-center justify-center p-4">
          <video
            src={fileUrl}
            controls
            className="max-w-full max-h-full rounded-lg"
            onLoadedData={() => setLoading(false)}
            onError={() => {
              setLoading(false)
              setError(true)
            }}
          >
            Your browser does not support the video tag.
          </video>
        </div>
      )
    }

    // For other file types, show download option
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <DocumentIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-tech-100 mb-2">
            {fileName}
          </h3>
          <p className="text-gray-600 dark:text-tech-300 mb-6">
            This file type cannot be previewed. Click below to download or open in a new tab.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Download
            </button>
            <button
              onClick={handleOpenInNewTab}
              className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              <EyeIcon className="w-4 h-4" />
              Open in New Tab
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white dark:bg-tech-900 rounded-lg shadow-xl w-full max-w-7xl h-full max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-tech-700">
          <div className="flex items-center gap-3">
            {isImage && <PhotoIcon className="w-6 h-6 text-gray-600 dark:text-tech-300" />}
            {isPdf && <DocumentIcon className="w-6 h-6 text-gray-600 dark:text-tech-300" />}
            {!isImage && !isPdf && <DocumentIcon className="w-6 h-6 text-gray-600 dark:text-tech-300" />}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-tech-100">
                {title}
              </h2>
              <p className="text-sm text-gray-600 dark:text-tech-300">
                {fileName}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="p-2 text-gray-600 dark:text-tech-300 hover:text-gray-800 dark:hover:text-tech-100 hover:bg-gray-100 dark:hover:bg-tech-800 rounded-lg transition-colors"
              title="Download file"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
            </button>
            
            <button
              onClick={handleOpenInNewTab}
              className="p-2 text-gray-600 dark:text-tech-300 hover:text-gray-800 dark:hover:text-tech-100 hover:bg-gray-100 dark:hover:bg-tech-800 rounded-lg transition-colors"
              title="Open in new tab"
            >
              <EyeIcon className="w-5 h-5" />
            </button>

            <button
              onClick={onClose}
              className="p-2 text-gray-600 dark:text-tech-300 hover:text-gray-800 dark:hover:text-tech-100 hover:bg-gray-100 dark:hover:bg-tech-800 rounded-lg transition-colors"
              title="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 relative overflow-hidden min-h-0">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-tech-800">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-tech-300">Loading file...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-tech-800">
              <div className="text-center">
                <DocumentIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-tech-100 mb-2">
                  Failed to load file
                </h3>
                <p className="text-gray-600 dark:text-tech-300 mb-6">
                  There was an error loading this file. Try downloading it instead.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                    Download
                  </button>
                  <button
                    onClick={handleOpenInNewTab}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    <EyeIcon className="w-4 h-4" />
                    Open in New Tab
                  </button>
                </div>
              </div>
            </div>
          )}

          {!error && renderFileContent()}
        </div>
      </div>
    </div>
  )
}
